import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { studentService, Student } from '../../services/studentService';
import { billService, Bill, ResourceType } from '../../services/billService';
import { absenceService, AbsenceResponse } from '../../services/absenceService';
import { classeService, Classe } from '../../services/classeService';
import { scoreService, Score } from '../../services/scoreService';
import { courseService, Course } from '../../services/courseService';
import { bulletinService } from '../../services/bulletinService';
import { useModal } from '../../context/ModalContext';
import { Pencil, Trash2 } from 'lucide-react';
import TableActions from '../../components/TableActions';
import './StudentDetails.css';

const StudentDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showAlert, showConfirm } = useModal();

    const [student, setStudent] = useState<Student | null>(null);
    const [bills, setBills] = useState<Bill[]>([]);
    const [absences, setAbsences] = useState<AbsenceResponse[]>([]);
    const [scores, setScores] = useState<Score[]>([]);
    const [classe, setClasse] = useState<Classe | null>(null);
    const [courses, setCourses] = useState<Map<string, string>>(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (id) {
            loadData(id);
        }
    }, [id]);

    const loadData = async (studentId: string) => {
        setLoading(true);
        setError('');

        try {
            // 1. Fetch Student Details (Critical - if this fails, we can't show the page)
            const studentData = await studentService.getStudentById(studentId);
            setStudent(studentData);

            // 2. Fetch Class Info (Non-critical)
            if (studentData.classId) {
                try {
                    const classes = await classeService.getAllClasses();
                    const foundClass = classes.find(c => c.id === studentData.classId);
                    setClasse(foundClass || null);
                } catch (e) {
                    console.warn('Failed to load class info', e);
                }
            }

            // 3. Fetch Bills (Non-critical - handle 403)
            try {
                const billsData = await billService.getBillsByResource(ResourceType.STUDENT, studentId);
                setBills(billsData);
            } catch (e) {
                console.warn('Failed to load bills', e);
                // We can choose to set an error state for bills specifically if we want to show "Access Denied" in that section
            }

            // 4. Fetch Absences (Non-critical - handle 403)
            try {
                const absenceData = await absenceService.searchAbsences({
                    studentId: studentId,
                    size: 1000
                });
                setAbsences(absenceData.content);
            } catch (e) {
                console.warn('Failed to load absences', e);
            }

            // 5. Fetch Scores (Non-critical - handle 403)
            try {
                const scoresData = await scoreService.getScoresByStudent(studentId);
                setScores(scoresData);
            } catch (e) {
                console.warn('Failed to load scores', e);
            }

            // 6. Fetch Courses (Non-critical)
            try {
                const allCourses = await courseService.getAllCourses();
                const courseMap = new Map<string, string>();
                allCourses.forEach(c => {
                    if (c.id) courseMap.set(c.id, c.name);
                });
                setCourses(courseMap);
            } catch (e) {
                console.warn('Failed to load courses', e);
            }

        } catch (e: any) {
            // Only fail completely if the main student data fetch fails
            setError(e.message || 'Failed to load student data');
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAbsence = (absenceId: string) => {
        showConfirm(t('common.delete'), t('absences.confirmDelete'), async () => {
            try {
                await absenceService.deleteAbsence(absenceId);
                setAbsences(prev => prev.filter(a => a.id !== absenceId));
            } catch (e) {
                console.error('Failed to delete absence', e);
                showAlert(t('common.error'), t('absences.failedToDelete'));
            }
        });
    };

    const handleDownloadBulletin = async () => {
        if (!student) return;
        try {
            await bulletinService.generateBulletin({
                studentId: student.id,
                // Optional: Add date range logic here if needed
            });
        } catch (e) {
            showAlert(t('common.error'), t('bulletin.failedToGenerate'));
        }
    };

    const calculateTotalAbsenceHours = () => {
        let totalMinutes = 0;
        absences.forEach(absence => {
            const start = new Date(absence.period.start);
            const end = new Date(absence.period.end);
            const diffMs = end.getTime() - start.getTime();
            totalMinutes += Math.floor(diffMs / 60000);
        });

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}h ${minutes > 0 ? minutes + 'm' : ''}`;
    };

    // Edit Score Logic
    const [editingScore, setEditingScore] = useState<Score | null>(null);
    const [editScoreValue, setEditScoreValue] = useState<number | string>('');
    const [editScoreDate, setEditScoreDate] = useState<string>('');

    const handleEditScore = (score: Score) => {
        setEditingScore(score);
        setEditScoreValue(score.score);
        // Format date for input type="date" (YYYY-MM-DD)
        const date = new Date(score.examDate);
        const formattedDate = date.toISOString().split('T')[0];
        setEditScoreDate(formattedDate);
    };

    const handleCancelEditScore = () => {
        setEditingScore(null);
        setEditScoreValue('');
        setEditScoreDate('');
    };

    const handleSaveScore = async () => {
        if (!editingScore) return;

        const numValue = typeof editScoreValue === 'string' ? parseFloat(editScoreValue) : editScoreValue;
        if (isNaN(numValue) || numValue < 0 || numValue > 20) {
            showAlert(t('common.error'), t('scores.invalidScore'));
            return;
        }

        if (!editScoreDate) {
            showAlert(t('common.error'), t('scores.invalidDate'));
            return;
        }

        try {
            const updatedScore = {
                ...editingScore,
                score: numValue,
                examDate: new Date(editScoreDate).toISOString()
            };
            await scoreService.updateScore(editingScore.id!, updatedScore);

            // Update local state
            setScores(prev => prev.map(s => s.id === editingScore.id ? updatedScore : s));
            handleCancelEditScore();
        } catch (e) {
            console.error('Failed to update score', e);
            showAlert(t('common.error'), t('scores.failedToUpdate'));
        }
    };

    const handleDeleteScore = async (scoreId: string) => {
        showConfirm(t('common.delete'), t('scores.confirmDelete'), async () => {
            try {
                await scoreService.deleteScore(scoreId);
                setScores(prev => prev.filter(s => s.id !== scoreId));
            } catch (e) {
                console.error('Failed to delete score', e);
                showAlert(t('common.error'), t('scores.failedToDelete'));
            }
        });
    };

    if (loading) return <div className="student-details-container">{t('common.loading')}</div>;
    if (error) return <div className="student-details-container error">{error}</div>;
    if (!student) return <div className="student-details-container">{t('students.notFound')}</div>;

    return (
        <div className="student-details-container">
            <div className="student-details-header">
                <button className="btn-back" onClick={() => navigate(-1)}>
                    ←
                </button>
                <h1>{student.firstName} {student.lastName}</h1>
            </div>

            <div className="details-grid">
                {/* Personal Information */}
                <div className="details-card section-personal-info">
                    <div className="card-header">
                        <h2>{t('studentDetails.personalInfo')}</h2>
                    </div>
                    <div className="info-grid">
                        <div className="info-item">
                            <span className="info-label">{t('students.userName')}</span>
                            <span className="info-value">{student.userName}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">{t('studentDetails.class')}</span>
                            <span className="info-value">{classe ? classe.name : student.classId || '-'}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">{t('students.birthdate')}</span>
                            <span className="info-value">
                                {student.birthdate ? new Date(student.birthdate).toLocaleDateString() : '-'}
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">{t('studentDetails.status')}</span>
                            <span className="info-value">
                                {student.enabled ? t('common.active') : t('common.inactive')}
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">{t('studentDetails.lastLogin')}</span>
                            <span className="info-value">
                                {student.lastAuthenticationDatetime
                                    ? new Date(student.lastAuthenticationDatetime).toLocaleString()
                                    : '-'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Scores */}
                <div className="details-card section-scores">
                    <div className="card-header">
                        <h2>{t('studentDetails.scores')}</h2>
                        <button
                            className="btn-view-all"
                            onClick={handleDownloadBulletin}
                        >
                            {t('studentDetails.downloadBulletin')}
                        </button>
                    </div>
                    {scores.length === 0 ? (
                        <div className="no-data">{t('scores.noScores')}</div>
                    ) : (
                        <div className="table-container">
                            <table className="details-table">
                                <thead>
                                    <tr>
                                        <th>{t('scores.course')}</th>
                                        <th>{t('scores.score')}</th>
                                        <th>{t('scores.examDate')}</th>
                                        <th>{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scores.map(score => (
                                        <tr key={score.id}>
                                            <td>{courses.get(score.courseId) || score.courseId}</td>
                                            <td>{score.score}</td>
                                            <td>{new Date(score.examDate).toLocaleDateString()}</td>
                                            <td>
                                                <TableActions actions={[
                                                    {
                                                        label: t('common.edit'),
                                                        onClick: () => handleEditScore(score),
                                                        className: 'btn-edit-sm',
                                                        icon: <Pencil size={16} />,
                                                        hideLabelDesktop: true
                                                    },
                                                    {
                                                        label: t('common.delete'),
                                                        onClick: () => handleDeleteScore(score.id!),
                                                        className: 'btn-delete-sm',
                                                        icon: <Trash2 size={16} />,
                                                        hideLabelDesktop: true,
                                                        variant: 'danger'
                                                    }
                                                ]} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Bills */}
                <div className="details-card section-bills">
                    <div className="card-header">
                        <h2>{t('bills.title')}</h2>
                        <button
                            className="btn-view-all"
                            onClick={() => navigate(`/bills?resourceType=STUDENT&resourceId=${student.id}`)}
                        >
                            {t('studentDetails.viewAllBills')}
                        </button>
                    </div>
                    {bills.length === 0 ? (
                        <div className="no-data">{t('bills.noBills')}</div>
                    ) : (
                        <table className="details-table">
                            <thead>
                                <tr>
                                    <th>{t('bills.date')}</th>
                                    <th>{t('bills.amount')}</th>
                                    <th>{t('bills.status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bills.map(bill => (
                                    <tr key={bill.id}>
                                        <td>{bill.creationDatetime ? new Date(bill.creationDatetime).toLocaleDateString() : '-'}</td>
                                        <td>{bill.amount}</td>
                                        <td>
                                            <span className={`status-badge status-${bill.status.toLowerCase()}`}>
                                                {t(`bills.status.${bill.status}`)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Absences */}
                <div className="details-card section-absences">
                    <div className="card-header">
                        <h2>{t('absences.title')}</h2>
                    </div>

                    <div className="absence-summary">
                        <span className="total-hours-label">{t('absences.totalHours')}</span>
                        <span className="total-hours-value">{calculateTotalAbsenceHours()}</span>
                    </div>

                    {absences.length === 0 ? (
                        <div className="no-data">{t('absences.noAbsences')}</div>
                    ) : (
                        <div style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '16px' }}>
                            <table className="details-table">
                                <thead>
                                    <tr>
                                        <th>{t('absences.date')}</th>
                                        <th>{t('absences.period')}</th>
                                        <th>{t('absences.lesson')}</th>
                                        <th>{t('absences.duration')}</th>
                                        <th>{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {absences.map(absence => {
                                        const start = new Date(absence.period.start);
                                        const end = new Date(absence.period.end);
                                        const durationMins = Math.floor((end.getTime() - start.getTime()) / 60000);

                                        return (
                                            <tr key={absence.id}>
                                                <td>{start.toLocaleDateString()}</td>
                                                <td>
                                                    {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} -
                                                    {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </td>
                                                <td>{absence.courseName}</td>
                                                <td>{durationMins}m</td>
                                                <td>
                                                    <TableActions actions={[
                                                        {
                                                            label: t('common.delete'),
                                                            onClick: () => handleDeleteAbsence(absence.id),
                                                            className: 'btn-delete-sm',
                                                            icon: <Trash2 size={16} />,
                                                            hideLabelDesktop: true,
                                                            variant: 'danger'
                                                        }
                                                    ]} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Edit Score Modal */}
            {editingScore && (
                <div className="modal-overlay" onClick={handleCancelEditScore}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h3>{t('scores.editScore')}</h3>
                        <div className="form-group">
                            <label>{t('scores.course')}: {courses.get(editingScore.courseId) || editingScore.courseId}</label>
                            <div className="form-field">
                                <label>{t('scores.score')} (0-20)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="20"
                                    step="0.5"
                                    value={editScoreValue}
                                    onChange={e => setEditScoreValue(e.target.value)}
                                />
                            </div>
                            <div className="form-field">
                                <label>{t('scores.examDate')}</label>
                                <input
                                    type="date"
                                    value={editScoreDate}
                                    onChange={e => setEditScoreDate(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button className="btn-cancel" onClick={handleCancelEditScore}>
                                {t('common.cancel')}
                            </button>
                            <button className="btn-save" onClick={handleSaveScore}>
                                {t('common.save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentDetails;
